import { useContext, useEffect, useState } from 'react'
import cx from 'classnames'

import { SanityCart } from '@data/sanity/queries/types/site'
import { CartContext } from '@lib/cart/context'
import { ErrorMessages } from '@lib/helpers'
import { ShopContext } from '@lib/shop'

import CartItem from '@blocks/shop/cart-item'
import CommentField from './comment-field'
import CartEmpty from './empty'
import CartSubmit from './submit'
import CartSummary from './summary'
import VatIdField from './vat-id-field'

interface CartFormProps {
  cartSettings: SanityCart
}

const CartForm = ({ cartSettings }: CartFormProps) => {
  const { cart, isCartUpdating, isCartSubmitting } = useContext(CartContext)

  const [vatId, setVatId] = useState('')
  const [comment, setComment] = useState('')
  const [errorMessages, setErrorMessages] = useState<ErrorMessages>({})
  const { currencyCode } = useContext(ShopContext)

  const handleVatIdChange = (newValue: string) => setVatId(newValue)
  const handleCommentChange = (newValue: string) => setComment(newValue)

  useEffect(() => {
    window.gtag?.('event', 'view_cart', {
      currency: currencyCode,
      value:
        cart.lineItems.reduce((total, item) => {
          return total + item.quantity * item.price
        }, 0) / 100,
      items: cart.lineItems.map((item) => ({
        item_id: item.sku ?? item.id,
        item_name: `${item.product.title} - ${item.title}`,
        item_variant: item.title,
        price: item.price / 100,
        discount:
          item.comparePrice > 0 ? (item.comparePrice - item.price) / 100 : 0,
        quantity: item.quantity,
      })),
    })
  }, [])

  return (
    <div
      className={cx('container pb-8 lg:px-20', {
        'cursor-wait': isCartUpdating || isCartSubmitting,
      })}
    >
      <div className="flex flex-col relative h-full w-full">
        {cart.id && cart.lineItems.length === 0 && (
          <div>
            <div className="flex-1 flex flex-col py-4 sm:py-8 overflow-y-auto">
              <CartEmpty />
            </div>
          </div>
        )}

        {cart.id && cart.lineItems.length > 0 && (
          <div className="relative grid md:grid-cols-2 gap-x-10">
            <div>
              <div className="flex-1 flex flex-col py-4 sm:py-8 overflow-y-auto">
                <div>
                  {cart.lineItems.map((item) => (
                    <CartItem
                      key={item.id}
                      item={item}
                      inModal={false}
                      className="mb-10"
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="py-4 sm:py-8">
              <div className="relative pb-4 sm:pb-8">
                {cartSettings.showVatId && (
                  <VatIdField
                    inModal={false}
                    errorMessages={errorMessages}
                    setErrorMessages={setErrorMessages}
                    onChange={handleVatIdChange}
                  />
                )}

                {cartSettings.showComment && (
                  <CommentField
                    onChange={handleCommentChange}
                    inModal={false}
                  />
                )}

                <CartSummary />

                <CartSubmit
                  terms={cartSettings.terms}
                  storeUrl={cartSettings.storeURL}
                  setErrorMessages={setErrorMessages}
                  vatId={vatId}
                  comment={comment}
                />

                {cartSettings.message && (
                  <p className="mt-4 text-center text-sm sm:text-base">
                    {cartSettings.message}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default CartForm
